import React, { useEffect, useState } from 'react';
import { Separator } from '../../ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { OrdersByStatus } from './OrdersByStatus';
import { OrdersByDay } from './OrdersByDay';
import { ActiveIssues } from './ActiveIssues';
import { ArrowUpRight, Calendar, MoveRight, TrendingDown, TrendingUp } from 'lucide-react';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import Cookies from 'js-cookie';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { TOKEN_NAME } from '../../../constants';
import { getDates, getGreeting } from './home.helpers';
import InActiveOrders from './InActiveOrders';
import CancelledOrders from './CancelledOrders';
import MissingOrders from './MissingOrders';
import Claims from './Claims';
import { SelectCompanyModal } from './SelectCompanyModal';

const MerchantHome = () => {
  const [dashboardData, setDashboardData] = useState(null);

  const user = useSelector((state: { auth: { user: any } }) => state.auth.user);

  //const [loading, setLoading] = useState(true);
  //const [error, setError] = useState(null);
  const [range, setRange] = useState('last-7-days');
  const history = useHistory();

  // Function to fetch dashboard data
  const fetchDashboardStats = async () => {
    //setLoading(true);
    //setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URI}/dashboard?range=${range}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get(TOKEN_NAME)}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setDashboardData(data);
    } catch (err) {
      //setError(err.message);
    } finally {
      //setLoading(false);
    }
  };

  const openOrdersPage = () => {
    const { from, to } = getDates(range);
    history.push(`/orders?createdAtFrom=${from}&createdAtTo=${to}&statuses=ADDRESS_NOT_FOUND`);
  };

  // Fetch data on mount & when range changes
  useEffect(() => {
    if (user.selectedCompany) {
      fetchDashboardStats();
    }
  }, [range, user.selectedCompany]);

  return (
    <div className="bg-white min-h-screen">
      <div className="flex justify-center">
        <div className="w-full max-w-screen-xl bg-white">
          {/* Top Bar */}
          <div className="flex justify-between items-center">
            <div className="text-left">
              <h1 className="text-2xl font-semibold">
                {getGreeting()}, {user?.name}
              </h1>
              <p className="text-sm text-muted-foreground">Let&apos;s see how we&apos;re doing today</p>
            </div>

            {/* Select Button */}

            <Select
              value={range}
              onValueChange={(newValue) => setRange(newValue as 'last-7-days' | 'last-week')}
              disabled={!user.fullyTracked}
            >
              <SelectTrigger className="w-[180px] flex items-center">
                <span className="mr-2">
                  <Calendar className="h-4 w-4" />
                </span>
                <SelectValue placeholder="Last 7 days" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="last-7-days">Last 7 days</SelectItem>
                  <SelectItem value="last-week">Last week</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Divider Line */}
          <Separator className="mt-4" />

          {/* Cards Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Orders</CardTitle>
              </CardHeader>
              <CardContent className="flex justify-between items-center">
                <div>
                  <p className="text-3xl font-bold">{dashboardData?.totals.orders || 0}</p>
                  <p className="text-sm text-muted-foreground">Created in your account</p>
                </div>
                <div
                  className={`flex items-center ${dashboardData?.totals.ordersTrend > 0 ? 'text-green-500' : dashboardData?.totals.ordersTrend < 0 ? 'text-red-500' : 'text-orange-500'} self-start mt-1`}
                >
                  {dashboardData?.totals.ordersTrend > 0 ? (
                    <TrendingUp className="h-5 w-5" />
                  ) : dashboardData?.totals.ordersTrend < 0 ? (
                    <TrendingDown className="h-5 w-5" />
                  ) : (
                    <MoveRight className="h-5 w-5" />
                  )}

                  <span className="ml-1">{Math.abs(dashboardData?.totals.ordersTrend || 0).toFixed(0)}%</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Deliveries</CardTitle>
              </CardHeader>
              <CardContent className="flex justify-between items-center">
                <div>
                  <p className="text-3xl font-bold">{dashboardData?.totals.completed || 0}</p>
                  <p className="text-sm text-muted-foreground">Completed</p>
                </div>
                <div
                  className={`flex items-center ${dashboardData?.totals.completedTrend > 0 ? 'text-green-500' : dashboardData?.totals.completedTrend < 0 ? 'text-red-500' : 'text-orange-500'} self-start mt-1`}
                >
                  {dashboardData?.totals.completedTrend > 0 ? (
                    <TrendingUp className="h-5 w-5" />
                  ) : dashboardData?.totals.completedTrend < 0 ? (
                    <TrendingDown className="h-5 w-5" />
                  ) : (
                    <MoveRight className="h-5 w-5" />
                  )}
                  <span className="ml-1">{Math.abs(dashboardData?.totals.completedTrend || 0).toFixed(0)}%</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Address not found</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{dashboardData?.totals.addressNotFound || 0}</p>
                <div className="text-sm text-muted-foreground">
                  <div className="flex items-center">
                    {dashboardData?.totals.addressNotFound === 0 ? (
                      <span>All good!</span>
                    ) : (
                      <span>
                        Please{' '}
                        <span className="underline cursor-pointer" onClick={openOrdersPage}>
                          verify the addresses{' '}
                          <ArrowUpRight className="ml-1 inline h-4 w-4 cursor-pointer" onClick={openOrdersPage} />
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Issues</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{dashboardData?.totals.issues || 0}</p>
                <p className="text-sm text-muted-foreground">Created this period</p>
              </CardContent>
            </Card>
          </div>

          {/* Order bar charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {/* Orders by Day */}
            <OrdersByDay ordersByDay={dashboardData?.ordersByDay || {}} range={range} />

            {/* Orders By status */}
            <OrdersByStatus ordersByStatus={dashboardData?.ordersByStatus || {}} range={range} />
          </div>
          {user.fullyTracked && (
            <>
              {/* Issues */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <ActiveIssues />
                <InActiveOrders role="merchant" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <CancelledOrders />
                <MissingOrders />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <Claims />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MerchantHome;
